// src/auth/currentReturnTo.ts
//
// Canonical CRM helper: derive the current SAFE LOCAL return location
// (pathname + search + hash) to hand to the `/auth/start?returnTo=` re-mint
// flow (see `beginRpWorkspaceTransition`), so a successful auth/workspace round
// trip lands the user back on the route they were using — instead of the `/`
// default that made a deep route (e.g. `/phone/contact/:id`) reset to Home
// after a workspace switch or auth return.
//
// This is the CLIENT-side companion to the server `validateReturnTo`
// (server/src/auth/oidc/returnto). The SERVER remains the FINAL authority — it
// re-validates whatever we send and collapses anything off-origin to its own
// fallback. This helper's job is narrower and additive:
//
//   1. capture ONLY pathname+search+hash — never the absolute href, origin,
//      protocol, or host (those would be rejected by the server validator and
//      must never leave this process as a "returnTo"); and
//   2. refuse to capture an AUTH-PLUMBING route, which would create a
//      sign-in → returnTo → sign-in loop. The server validator would happily
//      preserve `/auth/callback`, so this loop fence MUST live on the client.
//
// Anything absent, off-origin-shaped, malformed, oversized, or auth-plumbing
// collapses to `/` (fail safe). Pure and unit-testable: pass a Location-like
// object; defaults to `window.location`.

/** Fallback destination — mirrors the server validator's default. */
const FALLBACK = '/';

/** Same length ceiling the server validator enforces. */
const MAX_LENGTH = 2048;

// Auth-plumbing routes must never be preserved as a post-auth destination:
// returning to one re-enters the flow. Matched on the leading path SEGMENT so
// `/auth/start`, `/auth/callback`, `/login`, `/logout` (and anything under
// `/auth/`) are fenced, WITHOUT over-excluding product routes that merely share
// a prefix (`/loginsettings` is NOT an auth route).
const AUTH_ROUTE_PREFIXES = ['/auth', '/login', '/logout'];

export interface ReturnLocationLike {
  pathname: string;
  search: string;
  hash: string;
}

/** True if the string contains any C0 control character or DEL (0x7F). */
function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) {
      return true;
    }
  }
  return false;
}

function isAuthPlumbingRoute(pathname: string): boolean {
  return AUTH_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Derive the current safe local return location (`pathname + search + hash`).
 *
 * Returns `/` when the location is absent, not a rooted same-origin path,
 * malformed, oversized, or an auth-plumbing route.
 *
 * @param loc a Location-like object; defaults to `window.location`.
 */
export function currentReturnTo(
  loc: ReturnLocationLike | undefined = typeof window !== 'undefined'
    ? window.location
    : undefined,
): string {
  const pathname = loc?.pathname ?? '';
  const search = loc?.search ?? '';
  const hash = loc?.hash ?? '';

  // Must be a rooted path. Reject protocol-relative (`//host`) and the
  // backslash variants browsers treat as `//` — defense in depth; the server
  // validator enforces the same, but we never want to SEND an unsafe value.
  if (
    !pathname.startsWith('/') ||
    pathname.startsWith('//') ||
    pathname.startsWith('/\\')
  ) {
    return FALLBACK;
  }

  const candidate = `${pathname}${search}${hash}`;

  if (
    candidate.length > MAX_LENGTH ||
    candidate.includes('\\') ||
    hasControlChars(candidate)
  ) {
    return FALLBACK;
  }

  // Loop safety: never send the user back into the auth flow.
  if (isAuthPlumbingRoute(pathname)) {
    return FALLBACK;
  }

  return candidate;
}
